type SvgDataUriParts = {
  metadata: string
  payload: string
  isBase64: boolean
}

const LIGHT_SCHEME_STYLE =
  '<style data-diagram-light-scheme="true">:root,svg{color-scheme:light;}</style>'
const WHITE_BACKGROUND_RECT =
  '<rect data-diagram-background="white" width="100%" height="100%" fill="#fff"/>'

const parseSvgDataUri = (src: string): SvgDataUriParts | null => {
  const match = src.match(/^data:image\/svg\+xml([^,]*),(.*)$/i)
  if (!match) return null

  const metadata = match[1] ?? ""
  return {
    metadata,
    payload: match[2] ?? "",
    isBase64: /;base64/i.test(metadata),
  }
}

const decodeUtf8FromBase64 = (value: string): string => {
  const binary = atob(value)
  const encoded = Array.from(binary, (char) =>
    `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`
  ).join("")

  return decodeURIComponent(encoded)
}

const encodeUtf8ForBase64 = (value: string): string =>
  encodeURIComponent(value).replace(/%([0-9A-F]{2})/g, (_, hex: string) =>
    String.fromCharCode(Number.parseInt(hex, 16))
  )

const decodeSvgPayload = (parts: SvgDataUriParts): string | null => {
  try {
    if (parts.isBase64) {
      return decodeUtf8FromBase64(parts.payload)
    }

    return decodeURIComponent(parts.payload)
  } catch {
    return null
  }
}

const encodeSvgDataUri = (svg: string, parts: SvgDataUriParts): string => {
  if (parts.isBase64) {
    return `data:image/svg+xml${parts.metadata},${btoa(encodeUtf8ForBase64(svg))}`
  }

  return `data:image/svg+xml${parts.metadata},${encodeURIComponent(svg)}`
}

const injectLightCanvas = (svg: string): string | null => {
  const svgTag = svg.match(/<svg\b[^>]*>/i)
  const svgTagIndex = svgTag?.index
  const svgTagValue = svgTag?.[0]
  if (svgTagIndex == null || !svgTagValue) return null

  const insertAt = svgTagIndex + svgTagValue.length
  const additions = [
    svg.includes('data-diagram-light-scheme="true"')
      ? ""
      : LIGHT_SCHEME_STYLE,
    svg.includes('data-diagram-background="white"')
      ? ""
      : WHITE_BACKGROUND_RECT,
  ].join("")

  if (!additions) return svg

  return `${svg.slice(0, insertAt)}${additions}${svg.slice(insertAt)}`
}

export const normalizeDiagramSvgDataUri = (src: string): string => {
  const parts = parseSvgDataUri(src)
  if (!parts) return src

  const svg = decodeSvgPayload(parts)
  if (!svg) return src

  const normalizedSvg = injectLightCanvas(svg)
  if (!normalizedSvg || normalizedSvg === svg) return src

  return encodeSvgDataUri(normalizedSvg, parts)
}
